import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  InputField,
  Modal,
  Select,
  SelectionBoxGroup,
} from '@enamelai/design-system'
import type { ScheduledReportRow } from '@constants/Reports/presetsAutomation.constants'
import {
  getDefaultPeriodForLatestData,
  getPeriodToolbarMenuForLatestData,
  isPeriodIdAllowedForLatestData,
  REPORTS_TOOLBAR_MENUS,
  type ReportsToolbarMenuEntry,
} from '@constants/Reports/reportFilterToolbarMenus.constants'
import {
  buildScheduleSummaryLine,
  MONTH_DAY_OPTIONS,
  type MonthDayChoice,
  ordinalSuffix,
  parseScheduleFromSummaryLine,
  type ScheduleInterval,
  SCHEDULE_INTERVAL_OPTIONS,
  type WeekdayChoice,
  WEEKDAY_OPTIONS,
} from '@components/pages/Reports/shared/ScheduleReportModal.component'

export type EditScheduledReportValues = {
  name: string
  toolbarSelection: Record<string, string>
  summaryLine: string
  scheduleInterval: ScheduleInterval
  scheduleWeekday: WeekdayChoice
  scheduleMonthDayChoice: MonthDayChoice
  scheduleCustomDay: number
}

type EditScheduledReportModalProps = {
  show: boolean
  onHide: () => void
  scheduledReport: ScheduledReportRow | null
  initialToolbarSelection?: Record<string, string>
  onSave: (values: EditScheduledReportValues) => void
}

type MenuItem = Extract<ReportsToolbarMenuEntry, { type: 'item' }>

function menuItems(entries: ReportsToolbarMenuEntry[]): MenuItem[] {
  return entries.filter((entry): entry is MenuItem => entry.type === 'item')
}

function EditScheduledReportModal({
  show,
  onHide,
  scheduledReport,
  initialToolbarSelection,
  onSave,
}: EditScheduledReportModalProps): React.ReactElement {
  const [name, setName] = useState('')
  const [latestData, setLatestData] = useState('historical')
  const [period, setPeriod] = useState('last-90-days')
  const [provider, setProvider] = useState('all-providers')
  const [interval, setInterval] = useState<ScheduleInterval>('month')
  const [weekday, setWeekday] = useState<WeekdayChoice>('friday')
  const [monthDayChoice, setMonthDayChoice] = useState<MonthDayChoice>('custom')
  const [customDay, setCustomDay] = useState(12)

  useEffect(() => {
    if (!show || !scheduledReport) return
    const selection = {
      'period-category': 'historical',
      'period-preset': 'last-90-days',
      'provider-filter': 'all-providers',
      ...(initialToolbarSelection ?? {}),
      ...(scheduledReport.toolbarSelection ?? {}),
    }
    const nextLatest = selection['period-category'] ?? 'historical'
    const nextPeriod = selection['period-preset'] ?? ''
    const schedule = parseScheduleFromSummaryLine(scheduledReport.summaryLine)
    setName(scheduledReport.name)
    setLatestData(nextLatest)
    setPeriod(
      isPeriodIdAllowedForLatestData(nextPeriod, nextLatest)
        ? nextPeriod
        : getDefaultPeriodForLatestData(nextLatest),
    )
    setProvider(selection['provider-filter'] ?? 'all-providers')
    setInterval(schedule.interval)
    setWeekday(schedule.weekday)
    setMonthDayChoice(schedule.monthDayChoice)
    setCustomDay(schedule.customDay)
  }, [show, scheduledReport, initialToolbarSelection])

  const latestDataOptions = useMemo(
    () =>
      menuItems(REPORTS_TOOLBAR_MENUS['period-category'] ?? [])
        .filter(
          (item) =>
            item.id === 'latest-data' ||
            item.id === 'historical' ||
            item.id === 'snapshot',
        )
        .map((item) => ({ value: item.id, label: item.label })),
    [],
  )
  const periodOptions = useMemo(
    () =>
      menuItems(getPeriodToolbarMenuForLatestData(latestData)).map((item) => ({
        value: item.id,
        label: item.label,
      })),
    [latestData],
  )
  const providerOptions = useMemo(
    () =>
      menuItems(REPORTS_TOOLBAR_MENUS['provider-filter'] ?? []).map((item) => ({
        value: item.id,
        label: item.label,
      })),
    [],
  )

  const handleLatestDataChange = useCallback((next: string) => {
    setLatestData(next)
    setPeriod((previous) =>
      isPeriodIdAllowedForLatestData(previous, next)
        ? previous
        : getDefaultPeriodForLatestData(next),
    )
  }, [])

  const customDayInvalid =
    interval === 'month' &&
    monthDayChoice === 'custom' &&
    (customDay < 1 || customDay > 31 || Number.isNaN(customDay))
  const nameInvalid = name.trim().length === 0

  const handleSave = useCallback(() => {
    if (!scheduledReport) return
    const trimmed = name.trim()
    if (!trimmed || customDayInvalid) return
    onSave({
      name: trimmed,
      toolbarSelection: {
        ...(initialToolbarSelection ?? {}),
        ...(scheduledReport.toolbarSelection ?? {}),
        'period-category': latestData,
        'period-preset': period,
        'provider-filter': provider,
      },
      summaryLine: buildScheduleSummaryLine(
        trimmed,
        interval,
        monthDayChoice,
        customDay,
        weekday,
      ),
      scheduleInterval: interval,
      scheduleWeekday: weekday,
      scheduleMonthDayChoice: monthDayChoice,
      scheduleCustomDay: customDay,
    })
    onHide()
  }, [
    customDay,
    customDayInvalid,
    initialToolbarSelection,
    interval,
    latestData,
    monthDayChoice,
    name,
    onHide,
    onSave,
    period,
    provider,
    scheduledReport,
    weekday,
  ])

  return (
    <Modal
      open={show}
      onClose={onHide}
      title="Edit scheduled report"
      size="lg"
      className="reports-edit-saved-view-modal reports-schedule-report-modal reports-edit-scheduled-report-modal"
      bodyClassName="reports-edit-saved-view-modal__body"
      secondaryLabel="Cancel"
      primaryLabel="Save changes"
      primaryDisabled={nameInvalid || customDayInvalid || !scheduledReport}
      onSecondaryClick={onHide}
      onPrimaryClick={handleSave}
    >
      <InputField
        id="edit-scheduled-report-name"
        label="Report name"
        variant="text"
        value={name}
        onChange={(event) => setName(event.target.value)}
        error={nameInvalid}
        helperText={nameInvalid ? 'Report name is required.' : undefined}
        fullWidth
        clearable={false}
      />

      <div className="reports-edit-saved-view-modal__field">
        <span className="reports-edit-saved-view-modal__label">
          Report period
        </span>
        <div className="reports-edit-saved-view-modal__row">
          <Select
            options={latestDataOptions}
            value={latestData}
            onChange={(value) => handleLatestDataChange(String(value))}
          />
          <Select
            options={periodOptions}
            value={period}
            onChange={(value) => setPeriod(String(value))}
          />
        </div>
      </div>

      <Select
        label="Report data"
        options={providerOptions}
        value={provider}
        onChange={(value) => setProvider(String(value))}
      />

      <div className="reports-edit-saved-view-modal__field">
        <span className="reports-edit-saved-view-modal__label">
          Report schedule
        </span>
        <div className="reports-schedule-report-modal__card">
          <SelectionBoxGroup
            name="edit-scheduled-interval"
            label="Generate this report every:"
            value={interval}
            options={SCHEDULE_INTERVAL_OPTIONS}
            onChange={(value) => setInterval(value as ScheduleInterval)}
            className="reports-schedule-report-modal__selection-boxes"
          />
        </div>

        {interval === 'week' ? (
          <div className="reports-schedule-report-modal__card">
            <SelectionBoxGroup
              name="edit-scheduled-weekday"
              label="Select a day of the week"
              value={weekday}
              options={WEEKDAY_OPTIONS.map(({ id, label }) => ({
                value: id,
                title: label,
              }))}
              onChange={(value) => setWeekday(value as WeekdayChoice)}
              className="reports-schedule-report-modal__selection-boxes"
            />
          </div>
        ) : null}

        {interval === 'month' ? (
          <>
            <div className="reports-schedule-report-modal__card">
              <SelectionBoxGroup
                name="edit-scheduled-month-day"
                label="Select a day of the month"
                value={monthDayChoice}
                options={MONTH_DAY_OPTIONS}
                onChange={(value) => setMonthDayChoice(value as MonthDayChoice)}
                className="reports-schedule-report-modal__selection-boxes"
              />
            </div>
            {monthDayChoice === 'custom' ? (
              <div className="reports-schedule-report-modal__card">
                <span className="reports-schedule-report-modal__card-label">
                  Set a custom day of the month
                </span>
                <div className="reports-schedule-report-modal__custom-day">
                  <span className="reports-schedule-report-modal__custom-day-prefix">
                    Every:
                  </span>
                  <InputField
                    id="edit-scheduled-custom-day-input"
                    label=""
                    aria-label="Custom day of the month"
                    variant="number"
                    numberMin={1}
                    numberMax={31}
                    className="reports-schedule-report-modal__custom-day-input"
                    value={customDay}
                    error={customDayInvalid}
                    helperText={
                      customDayInvalid ? 'Enter a day from 1 to 31.' : undefined
                    }
                    onChange={(event) =>
                      setCustomDay(parseInt(event.target.value, 10) || 0)
                    }
                    clearable={false}
                  />
                  <span className="reports-schedule-report-modal__custom-day-suffix">
                    {customDay >= 1 && customDay <= 31
                      ? ordinalSuffix(customDay)
                      : 'th'}{' '}
                    day
                  </span>
                </div>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </Modal>
  )
}

export default EditScheduledReportModal
